import { useDispatch } from 'react-redux'
import { useAsync } from 'react-async-hook'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '../common/Tooltip'
import Circle from '@mui/icons-material/FiberManualRecord'
import HelpOutline from '@mui/icons-material/HelpOutline'
import { ListServiceSortableField, Service } from 'common/api/v1/types'
import { usePageParamsFiltered, useSettingsSelector } from '../../utils'
import { Link } from '../common/Link'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { getServices } from '../../redux/actions/serviceActions'
import { ServicesRequestParams } from '../../api/nm-types'
import { Api, AppDispatch, useRoutes } from '../../store'
import {
  ListK8sServiceFilterKey,
  makeListK8sServicesFilter,
  mapK8sFilterToUrlParam,
  mapUrlParamToK8sServiceFilter,
} from './listServiceFilter'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import Pendable from '../common/Pendable'
import DataArrayIcon from '@mui/icons-material/DataArray'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SettingsIcon from '@mui/icons-material/Settings'
import { getProductLink } from './Product'
import { ActionMenu } from '../common/Table/ActionMenu'
const { serviceApi } = Api

const Toggle = ({ value }: { value: boolean }) => {
  return value ? <Circle /> : null
}

export const ListView = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { devMode } = useSettingsSelector()
  const searchParams = { includeHidden: devMode, ...usePageParamsFiltered<ServicesRequestParams>() }
  const { result } = useAsync(() => getServices(searchParams, { dispatch, serviceApi }), [JSON.stringify(searchParams)])
  const routes = useRoutes()
  const tableConfig: TableConfig<Service, ListServiceSortableField> = [
    {
      title: 'product',
      getValue: (service) => (
        <Link to={routes.servicesUpdate({ id: service.id })} underline="hover">
          {service.product}
        </Link>
      ),
      sorting: { byParameter: ListServiceSortableField.product },
    },
    {
      title: 'purpose',
      getValue: ({ purpose, description }) => (
        <Box sx={{ display: 'flex' }}>
          {purpose}
          {description && (
            <Tooltip title={description}>
              <HelpOutline sx={{ marginLeft: 1 }} />
            </Tooltip>
          )}
        </Box>
      ),
      sorting: { byParameter: ListServiceSortableField.purpose },
    },
    {
      title: 'collect logs',
      getValue: ({ collectLogs }) => <Toggle value={collectLogs} />,
      props: { noTypography: true },
    },
    {
      getValue: (service) => {
        const productLink = getProductLink(service.product)

        return (
          <ActionMenu
            options={[
              {
                label: 'Edit',
                icon: <SettingsIcon />,
                href: routes.servicesUpdate({ id: service.id }),
              },
              {
                label: 'View logs',
                icon: <DataArrayIcon />,
                href: service.filterUrl,
                openInNewTab: true,
              },
              ...(productLink
                ? [
                    {
                      label: productLink.tooltip,
                      icon: <OpenInNewIcon />,
                      href: productLink.href,
                      openInNewTab: true,
                    },
                  ]
                : []),
            ]}
          />
        )
      },
      props: { align: 'right', noTypography: true },
    },
  ]

  const filters = makeListK8sServicesFilter()
  return (
    <Wrapper name="Services">
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'k8sServices'}
            mapUrlParamToFilterFn={mapUrlParamToK8sServiceFilter}
            mapFilterToUrlParamFn={mapK8sFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListK8sServiceFilterKey.name)!}
          />
        </div>
      </Toolbar>
      <Pendable pending={!result}>
        {result && (
          <Table<Service>
            emptyMessageComponent={<MissingContent message="No services available" />}
            config={tableConfig}
            data={result.items}
            id="services-table"
            pagination={{ total: result.total, useUrlSearchParams: true }}
          />
        )}
      </Pendable>
    </Wrapper>
  )
}
